// PCRiesgoTemplate.ts
// Genera el HTML del Dictamen de Riesgo a partir de variables.
// Basado en el contenido del HTML original del usuario.

export interface DictamenRiesgoVars {
  // Encabezado / remitente
  dictamenFolio: string;                 // ej: "PC/DR011/TOTOLAC/2025"
  area: string;                          // ej: "coordinación de proteccion civil"
  fechaLugar: string;                    // ej: "TOTOLAC, TLAX; AGOSTO 01 DE 2025"

  // Destinatario
  destinatarioNombre: string;            // ej: "C. NORMA TLAPALE AGUILAR"
  destinatarioCargo: string;             // ej: "PRESIDENTA DE COMUNIDAD"
  destinatarioUbicacion: string;         // ej: "DE ACXOTLA DEL RIO, TOTOLAC"
  destinatarioPresente?: string;         // por defecto: "presente"

  // Título
  titulo?: string;                       // por defecto: "dictamen de riesgo"

  // Variables que estaban en NEGRITAS en el cuerpo del texto
  lugarVerificacionTipo: string;         // <b>salón de usos múltiples</b>
  ubicacionReferencia: string;           // <b>cancha anexa de la iglesia de Acxotla del Rio</b>
  domicilio: string;                     // <b>Calle 20 de noviembre., Av. Juárez</b>
  comunidad: string;                     // <b>Acxotla del Rio</b>
  fechaVerificacion: string;             // <b>24/07/2025</b>
  hallazgos: string;                     // TEXTO EN NEGRITAS de hallazgos largos
  fundamentoLegalRiesgo: string;         // <b>Con fundamento en el Artículo 4 ...</b>
  existeRiesgoTexto: string;             // <b>EXISTE RIESGO</b> (o "NO EXISTE RIESGO")
  recomendacion: string;                 // Recomendación larga en NEGRITAS

  // Firmas
  firma1Nombre: string;                  // "LIC. IVAN LANDER CORONA"
  firma1Cargo: string;                   // "COORDINADOR DE PROTECCIÓN CIVIL"
  firma2Nombre: string;                  // "P. OPER. MAURICIO CRUZ BERNAL"
  firma2Cargo: string;                   // "VERIFICADOR"

  // Opcionales (assets)
  cssHref?: string;                      // si deseas inyectar tu CSS, ej: "estilos.css"
  scriptSrc?: string;                    // si deseas inyectar tu JS, ej: "scripts.js"
}

export function renderPCRiesgo(v: DictamenRiesgoVars): string {
  const destinatarioPresente = v.destinatarioPresente ?? 'presente';
  const titulo = v.titulo ?? 'dictamen de riesgo';

  // En este template se respetan las NEGRITAS exactamente donde estaban en el HTML original.
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Dictamen de Riesgo</title>
  ${v.cssHref ? `<link rel="stylesheet" href="${escapeHtml(v.cssHref)}">` : ''}
  ${v.scriptSrc ? `<script src="${escapeHtml(v.scriptSrc)}" defer></script>` : ''}
  <style>
    /* Estilos mínimos por si no se provee CSS externo */
    body { font-family: Arial, Helvetica, sans-serif; line-height: 1.4; color: #111; }
    section { margin: 16px 0; }
    .remitente div, .destinatario div { text-transform: uppercase; }
    .titulo { text-transform: uppercase; font-weight: bold; text-align: center; margin: 16px 0; }
    .atento { text-transform: uppercase; text-align: center; margin: 24px 0 8px; }
    .firmas { display: flex; gap: 24px; justify-content: center; margin-top: 24px; }
    .firmas > div { text-align: center; }
  </style>
</head>
<body>
  <section class="remitente">
    <div>DICTAMEN: ${escapeHtml(v.dictamenFolio)}</div>
    <div>Area: ${escapeHtml(v.area)}</div>
    <div>${escapeHtml(v.fechaLugar)}</div>
  </section>

  <section class="destinatario">
    <div>${escapeHtml(v.destinatarioNombre)}</div>
    <div>${escapeHtml(v.destinatarioCargo)}</div>
    <div>${escapeHtml(v.destinatarioUbicacion)}</div>
    <div>${escapeHtml(destinatarioPresente)}</div>
  </section>

  <section class="cuerpo">
    <div class="titulo">${escapeHtml(titulo)}</div>
    <div>
      De acuerdo a las facultades de la Coordinación de Protección Civil del H. Ayuntamiento de Totolac, Tlaxcala. 
      Así como el resultado de la verificación ocular del 
      <b>${escapeHtml(v.lugarVerificacionTipo)}</b> 
      ubicados en la <b>${escapeHtml(v.ubicacionReferencia)}</b> 
      con domicilio en <b>${escapeHtml(v.domicilio)}</b> 
      pertenecientes a la comunidad de <b>${escapeHtml(v.comunidad)}</b> de este municipio. 
      Basándose en la verificación ocular realizada el <b>${escapeHtml(v.fechaVerificacion)}</b> por esta coordinación, 
      con fundamento en los Artículos 5 Fracción II y III, y 48 del reglamento de la Ley de Protección Civil., 
      por medio del personal autorizado, donde <b>${escapeHtml(v.hallazgos)}</b> 
      Sirva la siguiente para dar cumplimiento a las disposiciones de Protección Civil Municipal para el resguardo íntegro de las personas 
      que acudan y ocupen el salón de usos múltiples. 
      <b>${escapeHtml(v.fundamentoLegalRiesgo)}</b>, 
      se verifico que si <b>${escapeHtml(v.existeRiesgoTexto)}</b>, 
      Dando cumplimiento a las disposiciones que Protección Civil requiere para el seguro y protección de la población. 
      Por lo anterior, la Coordinación de protección civil municipal a través del personal de verificación autorizado recomienda 
      <b>${escapeHtml(v.recomendacion)}</b>
    </div>
  </section>

  <div class="atento">atentamente</div>

  <section class="firmas">
    <div class="firma1">
      <div>${escapeHtml(v.firma1Nombre)}</div>
      <div>${escapeHtml(v.firma1Cargo)}</div>
    </div>
    <div class="firma2">
      <div>${escapeHtml(v.firma2Nombre)}</div>
      <div>${escapeHtml(v.firma2Cargo)}</div>
    </div>
  </section>
</body>
</html>`;
}

// Sencilla función para evitar inyección de HTML al insertar variables.
function escapeHtml(input: string): string {
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
